import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { basePath } from '@/utils/basePath';
import {
  PRICING_COMPARISON,
  PRICING_FEATURES,
  PRICING_PLANS,
} from '@/utils/data/princingFunctions';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

const cabinetPlan = PRICING_PLANS.find((p) => p.id === 'cabinet')!;
const collaborateurPlan = PRICING_PLANS.find((p) => p.id === 'collaborateur')!;

const TARIF_SEO: SEOProps = {
  title: 'Tarifs | zLawyer',
  description:
    "Découvrez le tarif zLawyer : 49€/mois/utilisateur, tout inclus. Vos collaborateurs bénéficient d'un accès dédié à 17€/mois.",
  url: `https://www.zlawyer.fr${basePath}/tarif/`,
};

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className="text-orange h-4 w-4 shrink-0"
    fill="none"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 8.5L6.5 12L13 5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

function StatusDot({ value }: { value: boolean | 'partial' }) {
  if (value === true)
    return <span className="bg-orange inline-block size-1.5 shrink-0 rounded-full" />;
  if (value === 'partial')
    return <span className="bg-dark-blue/25 inline-block size-1.5 shrink-0 rounded-full" />;
  return <span className="bg-dark-blue/15 inline-block size-1.5 shrink-0 rounded-full" />;
}

function collabValueClassName(value: boolean | 'partial') {
  if (value === false) return 'text-dark-blue/35 text-sm';
  return 'text-dark-blue/70 text-sm';
}

export default function TarifPage() {
  const heroRef = useRef<HTMLElement>(null);
  const heroTagRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroSubtitleRef = useRef(null);

  const plansRef = useRef<HTMLElement>(null);
  const mainCardRef = useRef<HTMLDivElement>(null);
  const collabBannerRef = useRef<HTMLDivElement>(null);

  const howRef = useRef<HTMLElement>(null);
  const howTagRef = useRef(null);
  const howTitleRef = useRef(null);
  const stepsRef = useRef<HTMLDivElement[]>([]);

  const tableRef = useRef<HTMLElement>(null);
  const tableTagRef = useRef(null);
  const tableTitleRef = useRef(null);
  const tableRowsRef = useRef<HTMLElement[]>([]);

  const ctaRef = useRef<HTMLElement>(null);
  const ctaTitleRef = useRef(null);
  const ctaButtonRef = useRef(null);

  useGSAP(
    () => {
      if (!heroRef.current) return;
      const splitTitle = new SplitText(heroTitleRef.current, { type: 'lines', mask: 'lines' });
      const splitSub = new SplitText(heroSubtitleRef.current, { type: 'lines', mask: 'lines' });
      gsap.set(heroTagRef.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top 65%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(heroTagRef.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .from(splitSub.lines, { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines }, '<+0.1');

      return () => {
        splitTitle.revert();
        splitSub.revert();
      };
    },
    { scope: heroRef },
  );

  useGSAP(
    () => {
      if (!plansRef.current) return;
      gsap.set([mainCardRef.current, collabBannerRef.current], { autoAlpha: 0, y: MOTION.y });

      gsap
        .timeline({
          defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
          scrollTrigger: {
            trigger: plansRef.current,
            start: 'top 65%',
            toggleActions: 'play none none reverse',
          },
        })
        .to(mainCardRef.current, { autoAlpha: 1, y: 0 })
        .to(collabBannerRef.current, { autoAlpha: 1, y: 0 }, '<+0.12');
    },
    { scope: plansRef },
  );

  useGSAP(
    () => {
      if (!howRef.current) return;
      const splitTitle = new SplitText(howTitleRef.current, { type: 'lines', mask: 'lines' });
      const stepEls = stepsRef.current.filter(Boolean);
      gsap.set(howTagRef.current, { autoAlpha: 0, y: 8 });
      gsap.set(stepEls, { autoAlpha: 0, y: MOTION.y });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: howRef.current,
          start: 'top 65%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(howTagRef.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .to(stepEls, { autoAlpha: 1, y: 0, stagger: 0.1 }, '<+0.2');

      return () => {
        splitTitle.revert();
      };
    },
    { scope: howRef },
  );

  useGSAP(
    () => {
      if (!tableRef.current) return;
      const splitTitle = new SplitText(tableTitleRef.current, { type: 'lines', mask: 'lines' });
      const rowEls = tableRowsRef.current.filter(Boolean);
      gsap.set(tableTagRef.current, { autoAlpha: 0, y: 8 });
      gsap.set(rowEls, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: tableRef.current,
          start: 'top 65%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(tableTagRef.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .to(rowEls, { autoAlpha: 1, y: 0, stagger: 0.04, duration: MOTION.durationSm }, '<+0.2');

      return () => {
        splitTitle.revert();
      };
    },
    { scope: tableRef },
  );

  useGSAP(
    () => {
      if (!ctaRef.current) return;
      const splitTitle = new SplitText(ctaTitleRef.current, { type: 'lines', mask: 'lines' });
      gsap.set(ctaButtonRef.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: ctaRef.current,
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.from(splitTitle.lines, { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines }).to(
        ctaButtonRef.current,
        { autoAlpha: 1, y: 0, duration: MOTION.durationSm },
        '<+0.2',
      );

      return () => {
        splitTitle.revert();
      };
    },
    { scope: ctaRef },
  );

  return (
    <div>
      {/* ── Section 1 : Hero ──────────────────────────────────────────────── */}
      <section ref={heroRef} className="max-width-blue-section" id="tarif-hero">
        <div className="flex max-w-3xl flex-col gap-5">
          <Tag ref={heroTagRef}>Tarification</Tag>

          <h1 ref={heroTitleRef} className="w-fit">
            Un tarif simple, tout inclus
          </h1>

          <p ref={heroSubtitleRef} className="text-dark-blue/70 leading-relaxed">
            Un seul abonnement par utilisateur, toutes les fonctionnalités incluses dès le premier
            jour. Si votre cabinet travaille avec des collaborateurs, ils bénéficient d&apos;un
            accès dédié à un tarif adapté à leur usage.
          </p>
        </div>
      </section>

      {/* ── Section 2 : Tarification ──────────────────────────────────────── */}
      <section ref={plansRef} className="max-width-white-section" id="tarif-plans">
        <div className="max-width-white-section-content flex flex-col gap-6">
          {/* Tarif principal */}
          <div
            ref={mainCardRef}
            className="border-dark-blue/8 flex flex-col gap-8 rounded-(--radius-current) border bg-white p-8 shadow-(--shadow-current) lg:p-10"
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col">
                <div className="flex items-end gap-1.5">
                  <span
                    className="text-orange font-roboto-bold leading-none tabular-nums"
                    style={{ fontSize: 'clamp(48px, 5.5vw, 64px)' }}
                  >
                    {cabinetPlan.price}€
                  </span>
                  <span className="text-dark-blue/40 mb-1.5 text-sm leading-snug">
                    /mois
                    <br />
                    /utilisateur
                  </span>
                </div>
                <p className="text-dark-blue/50 text-xs!">
                  Sans engagement · Résiliation à tout moment
                </p>
              </div>

              <Button href="/contact" variant="fill">
                Demandez une démo
              </Button>
            </div>

            <div className="bg-dark-blue/6 h-px w-full" />

            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {PRICING_FEATURES.map((feature) => (
                <li key={feature.label} className="flex items-center gap-3">
                  <CheckIcon />
                  <span className="text-dark-blue/80 text-sm leading-relaxed">{feature.label}</span>
                  {feature.inCollaborateur && (
                    <Tag className="ml-auto shrink-0 rounded-full border px-2 py-0.5 text-xs">
                      + Collab
                    </Tag>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Tarif collaborateur */}
          <div
            ref={collabBannerRef}
            className="border-dark-blue/6 bg-light-blue/40 flex flex-col gap-6 rounded-(--radius-current) border p-7 lg:flex-row lg:items-start lg:gap-12"
          >
            <div className="flex flex-col gap-2 lg:flex-1">
              <p className="text-dark-blue font-roboto-medium text-base leading-snug">
                Votre cabinet travaille avec des collaborateurs ?
              </p>
              <p className="text-dark-blue/55 text-sm leading-relaxed">
                Ils bénéficient d&apos;un accès à la plateforme à un tarif adapté à leur usage,
                connecté à votre cabinet. Offre réservée aux collaborateurs des cabinets équipés de
                zLawyer.
              </p>
            </div>

            <div className="border-dark-blue/8 flex flex-col gap-5 lg:min-w-[260px] lg:border-l lg:pl-12">
              <div className="flex items-end gap-1.5">
                <span
                  className="text-dark-blue/70 font-roboto-bold leading-none tabular-nums"
                  style={{ fontSize: 'clamp(32px, 3.5vw, 40px)' }}
                >
                  {collaborateurPlan.price}€
                </span>
                <span className="text-dark-blue/35 mb-1 text-sm leading-snug">
                  /mois
                  <br />
                  /collaborateur
                </span>
              </div>

              <p className="text-dark-blue/50 text-sm leading-relaxed">
                Accès aux fonctionnalités marquées{' '}
                <span className="text-orange border-orange/25 bg-orange/5 font-roboto-medium inline-block rounded-full border px-2 py-0.5 align-middle text-xs">
                  + Collab
                </span>{' '}
                dans la liste ci-dessus.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 3 : Comment ça fonctionne ────────────────────────────── */}
      <section ref={howRef} className="max-width-blue-section" id="tarif-fonctionnement">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-3">
            <Tag ref={howTagRef}>Fonctionnement</Tag>
            <h2 ref={howTitleRef} className="w-fit">
              Deux profils, une seule plateforme
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
            <div
              ref={(el) => {
                if (el) stepsRef.current[0] = el;
              }}
              className="border-dark-blue/8 flex flex-col gap-4 rounded-(--radius-current) border bg-white p-6"
            >
              <div className="bg-dark-blue/5 flex h-10 w-10 items-center justify-center rounded-full">
                <span className="text-dark-blue/40 font-roboto-bold text-sm">01</span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-dark-blue font-roboto-medium text-base leading-snug">
                  Le cabinet souscrit
                </p>
                <p className="text-dark-blue/50 text-sm leading-relaxed">
                  {cabinetPlan.price}€ /mois /utilisateur
                </p>
              </div>
            </div>

            <div
              ref={(el) => {
                if (el) stepsRef.current[1] = el;
              }}
              className="hidden items-center justify-center md:flex"
            >
              <svg
                aria-hidden="true"
                className="text-dark-blue/20 h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>

            <div
              ref={(el) => {
                if (el) stepsRef.current[2] = el;
              }}
              className="border-dark-blue/8 flex flex-col gap-4 rounded-(--radius-current) border bg-white p-6"
            >
              <div className="bg-dark-blue/5 flex h-10 w-10 items-center justify-center rounded-full">
                <span className="text-dark-blue/40 font-roboto-bold text-sm">02</span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-dark-blue font-roboto-medium text-base leading-snug">
                  Les collaborateurs rejoignent
                </p>
                <p className="text-dark-blue/50 text-sm leading-relaxed">
                  {collaborateurPlan.price}€ /mois /collaborateur
                </p>
              </div>
            </div>

            <div
              ref={(el) => {
                if (el) stepsRef.current[3] = el;
              }}
              className="hidden items-center justify-center md:flex"
            >
              <svg
                aria-hidden="true"
                className="text-dark-blue/20 h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>

            <div
              ref={(el) => {
                if (el) stepsRef.current[4] = el;
              }}
              className="border-dark-blue/8 flex flex-col gap-4 rounded-(--radius-current) border bg-white p-6"
            >
              <div className="bg-dark-blue/5 flex h-10 w-10 items-center justify-center rounded-full">
                <span className="text-dark-blue/40 font-roboto-bold text-sm">03</span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-dark-blue font-roboto-medium text-base leading-snug">
                  Toute l&apos;équipe est synchronisée
                </p>
                <p className="text-dark-blue/50 text-sm leading-relaxed">
                  Cabinet et collaborateurs, sur la même plateforme
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 4 : Tableau comparatif ───────────────────────────────── */}
      <section ref={tableRef} className="max-width-white-section" id="tarif-fonctionnalites">
        <div className="max-width-white-section-content flex flex-col gap-10">
          <div className="flex flex-col gap-3">
            <Tag ref={tableTagRef}>Fonctionnalités</Tag>
            <h2 ref={tableTitleRef} className="w-fit">
              Chaque profil, un usage adapté
            </h2>
            <p className="text-dark-blue/50 max-w-xl text-sm leading-relaxed">
              La même plateforme, des accès calibrés selon le rôle de chacun dans le cabinet.
            </p>
          </div>

          <div className="border-dark-blue/[0.07] w-full overflow-x-auto rounded-(--radius-current) border">
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className="border-dark-blue/[0.07] border-b">
                  <th className="w-1/3" />
                  <th className="w-1/3 px-5 py-4 text-left">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-dark-blue/70 font-roboto-medium text-sm">Cabinet</span>
                      <span className="text-dark-blue/40 text-xs font-normal tabular-nums">
                        {cabinetPlan.price}€ /mois /utilisateur
                      </span>
                    </div>
                  </th>
                  <th className="w-1/3 px-5 py-4 text-left">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-dark-blue/70 font-roboto-medium text-sm">
                        Collaborateur
                      </span>
                      <span className="text-dark-blue/40 text-xs font-normal tabular-nums">
                        {collaborateurPlan.price}€ /mois /collaborateur
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRICING_COMPARISON.map((row, index) => (
                  <tr
                    key={row.feature}
                    ref={(el) => {
                      if (el) tableRowsRef.current[index] = el;
                    }}
                    className="border-dark-blue/[0.05] hover:bg-dark-blue/[0.02] border-t transition-colors duration-150"
                  >
                    <td className="text-dark-blue/70 px-5 py-3.5 text-sm">{row.feature}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <StatusDot value={row.cabinetPositive} />
                        <span className="text-dark-blue/70 text-sm">{row.cabinetValue}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <StatusDot value={row.collabPositive} />
                        <span className={collabValueClassName(row.collabPositive)}>
                          {row.collabValue}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Section 5 : CTA ───────────────────────────────────────────────── */}
      <section ref={ctaRef} className="max-width-blue-section" id="tarif-cta">
        <div className="flex flex-col items-center gap-6 text-center">
          <h2 ref={ctaTitleRef} className="w-fit max-w-2xl">
            Prêt à simplifier la gestion de votre cabinet ?
          </h2>
          <div ref={ctaButtonRef}>
            <Button href="/contact" variant="fill">
              Demandez une démo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

TarifPage.seo = TARIF_SEO;
